'use client'
 
import { useState, useEffect } from 'react'
import { ColumnDef } from "@tanstack/react-table"
import { 
  ArrowUpDown, 
  MoreHorizontal, 
  Trash, 
  Loader2, 
  FileText, 
  Calendar, 
  ExternalLink, 
  CreditCard, 
  Clock, 
  AlertTriangle, 
  CheckCircle2, 
  CircleDollarSign,
  Upload
} from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import FormModal from "../../components/FormModal"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import BillsService from '@/api/bills.service'
import PaymentMethodService from '@/api/paymentmethods.service'
import ChartOfAccountsService from '@/api/chartofaccounts.service'
import TransactionsService from '@/api/transactions.service'
import { useToast } from "@/hooks/use-toast"
import { Separator } from "@/components/ui/separator"
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogFooter, AlertDialogDescription, AlertDialogTitle, AlertDialogContent, AlertDialogHeader } from '@/components/ui/alert-dialog'
import { canEdit, canDelete } from '@/lib/permissions'

export type BillType = {
  id: number
  publicId: string
  billNumber: string
  amount: number
  currency: string
  status: string
  issueDate: string
  dueDate: string
  timezone: string
  dateUpdated: string
  dateCreated: string
  billItems: BillItemType[]
  receiptNumber: string
  receiptDocumentLink: string
  supplier?: any
}

export type BillItemType = {
  id: number
  billId: number
  name: string
  description: string
  quantity: number
  unitPrice: number
  totalPrice: number
}

export const columns: ColumnDef<BillType>[] = [
  {
    accessorKey: "billNumber",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Bill Number
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="flex items-center">
        <FileText className="w-4 h-4 mr-2 text-slate-400" />
        <span className="font-medium text-slate-900 dark:text-slate-100">
          {cell.row.original.billNumber}
        </span>
      </div>
    ),
  },
  {
    accessorKey: "supplier",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Supplier
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const supplier = cell.getValue() as any;
      if (!supplier) return <span className="text-slate-500">-</span>;
      
      // Handle both new supplier schema and legacy customer schema
      const supplierName = supplier.name 
        ? supplier.name 
        : supplier.companyName 
          ? supplier.companyName 
          : `${supplier.firstName || ''} ${supplier.lastName || ''}`.trim();
      
      const contactPerson = supplier.contactPerson 
        ? supplier.contactPerson 
        : (supplier.firstName && supplier.lastName) 
          ? `${supplier.firstName} ${supplier.lastName}` 
          : '';
      
      return (
        <div className="flex flex-col">
          <span className="font-medium text-slate-900 dark:text-slate-100">
            {supplierName}
          </span>
          {contactPerson && supplierName !== contactPerson && (
            <span className="text-xs text-slate-500 dark:text-slate-400">
              {contactPerson}
            </span>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Status
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const status = cell.getValue() as string;
      const statusConfig = {
        'paid': {
          color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200 dark:border-emerald-800',
          icon: <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" />
        },
        'overpaid': {
          color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
          icon: <CircleDollarSign className="w-3.5 h-3.5 mr-1.5" />
        },
        'unpaid': {
          color: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border-rose-200 dark:border-rose-800',
          icon: <AlertTriangle className="w-3.5 h-3.5 mr-1.5" />
        },
        'partially_paid': {
          color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800',
          icon: <CreditCard className="w-3.5 h-3.5 mr-1.5" />
        },
        'overdue': {
          color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800',
          icon: <Clock className="w-3.5 h-3.5 mr-1.5" />
        },
        'pending': {
          color: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400 border-indigo-200 dark:border-indigo-800',
          icon: <Clock className="w-3.5 h-3.5 mr-1.5" />
        }
      };
      
      const config = statusConfig[status as keyof typeof statusConfig] || statusConfig['pending'];
      const formattedStatus = status.replace('_', ' ');
      
      return (
        <Badge variant="outline" className={`font-medium flex items-center gap-0.5 px-2.5 py-1 rounded-full ${config.color}`}>
          {config.icon}
          <span className="capitalize">{formattedStatus}</span>
        </Badge>
      );
    },
  },
  {
    accessorKey: "receiptNumber",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Receipt
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell, row }) => {
      const receiptNumber = cell.getValue() as string;
      const receiptDocumentLink = row.original.receiptDocumentLink;
      
      if (!receiptDocumentLink) {
        return <div className="text-slate-400 dark:text-slate-500 text-sm">No receipt</div>;
      }
      
      return (
        <div className="flex items-center">
          {receiptDocumentLink && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1.5 bg-blue-50 text-blue-600 hover:bg-blue-100 hover:text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-800/50 dark:hover:text-blue-300 dark:border-blue-800 transition-all"
              onClick={() => window.open(receiptDocumentLink, '_blank')}
              title="View Receipt Document"
            >
              <span className="font-medium">Open {receiptNumber}</span>
              <ExternalLink className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "amount",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Amount
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="font-medium text-slate-900 dark:text-slate-100">
        {cell.row.original.currency} {cell.row.original.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </div>
    ),
  },
  {
    accessorKey: "dateCreated",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Date Created
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const date = new Date(cell.getValue() as string);
      const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      const formattedTime = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
      
      return (
        <div className="text-slate-700 dark:text-slate-300">
          <div className="font-medium">{formattedDate}</div>
          <div className="text-xs text-slate-500 dark:text-slate-400">{formattedTime}</div>
        </div>
      );
    },
  },
  {
    accessorKey: "issueDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Issue Date
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      //const date = new Date(cell.getValue() as string);
      //const formattedDate = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
      
      return (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-slate-400" />
          <span className="text-slate-700 dark:text-slate-300">{cell.getValue() as string}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "dueDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Due Date
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const dueDate = cell.getValue() as string;
      const today = new Date();
      
      // Parse the date from the format dd/mm/yyyy
      const [day, month, year] = dueDate.split('/').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed in JS Date
      
      const isPastDue = date < today && cell.row.original.status !== 'paid' && cell.row.original.status !== 'overpaid';
      
      return (
        <div className="flex items-center">
          <Calendar className={`w-4 h-4 mr-2 ${isPastDue ? 'text-rose-500' : 'text-slate-400'}`} />
          <span className={`${isPastDue ? 'text-rose-600 font-medium' : 'text-slate-700 dark:text-slate-300'}`}>
            {dueDate}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "mark",
    header: "Payment",
    cell: ({ row }) => {
      const [isModalOpen, setModalOpen] = useState(false);
      const [isMarking, setIsMarking] = useState(false);
      const [amountPaid, setAmountPaid] = useState('');
      const [paymentMethodId, setPaymentMethodId] = useState('');
      const [paymentDate, setPaymentDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
      const [paymentReference, setPaymentReference] = useState('');
      const [paymentMethods, setPaymentMethods] = useState([]);
      const [isPaymentMethodsLoaded, setIsPaymentMethodsLoaded] = useState(false);
      const [receiptDocument, setReceiptDocument] = useState<File | null>(null);
      const [receiptDocumentProgress, setReceiptDocumentProgress] = useState(0);
      const [customReceiptNumber, setCustomReceiptNumber] = useState('');
      
      // Chart of accounts state
      const [chartOfAccounts, setChartOfAccounts] = useState<any[]>([]);
      const [selectedAccountId, setSelectedAccountId] = useState('');
      const [isChartOfAccountsLoaded, setIsChartOfAccountsLoaded] = useState(false);
      
      const [selectedBusiness] = useState(() => {
        const storedBusiness = typeof window !== 'undefined' ? window.localStorage.getItem('selectedBusiness') : null;
        return storedBusiness ? JSON.parse(storedBusiness) : null;
      });
      
      const { toast } = useToast();

      useEffect(() => {
        const fetchPaymentMethods = async () => {
          try {
            const response = await PaymentMethodService.getAllPaymentMethods();
            if (response.data.result === 'success') {
              setPaymentMethods(response.data.paymentMethods);
            } else {
              toast({
                title: "Error",
                description: "Failed to fetch payment methods",
                variant: "destructive",
              });
            }
          } catch (error) {
            toast({
              title: "Error",
              description: "Failed to fetch payment methods",
              variant: "destructive",
            });
          } finally {
            setIsPaymentMethodsLoaded(true);
          }
        };

        // Function to fetch chart of accounts
        const fetchChartOfAccounts = async () => {
          try {
            if (!selectedBusiness) return;
            const response = await ChartOfAccountsService.getAllChartOfAccounts(selectedBusiness.id);
            if (response.data.result === 'success') {
              // Filter to include only active accounts
              const activeAccounts = response.data.chartOfAccounts.filter(
                (account: any) => account.isActive
              );
              setChartOfAccounts(activeAccounts);
            } else {
              toast({
                title: "Error",
                description: "Failed to fetch chart of accounts",
                variant: "destructive",
              });
            }
          } catch (error) {
            console.error("Error fetching chart of accounts:", error);
            toast({
              title: "Error",
              description: "Failed to fetch chart of accounts",
              variant: "destructive",
            });
          } finally {
            setIsChartOfAccountsLoaded(true);
          }
        };
        
        if (isModalOpen) {
          fetchPaymentMethods();
          fetchChartOfAccounts();
        }
      }, [isModalOpen, selectedBusiness, toast]);

      const updateBillStatus = async (status: string, amount: any) => {
        try {
          setIsMarking(true);
          
          // Create form data
          const formData = new FormData();
          formData.append('status', status);
          formData.append('amount', amount);
          formData.append('paymentMethodId', paymentMethodId);
          
          // Format date to DD/MM/YYYY as expected by the API
          const formatDate = (dateStr: string) => {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
          };
          
          formData.append('paymentDate', formatDate(paymentDate));
          
          if (paymentReference) {
            formData.append('paymentReference', paymentReference);
          }
          
          if (customReceiptNumber) {
            formData.append('receiptNumber', customReceiptNumber);
          }
          
          if (receiptDocument) {
            formData.append('receiptDocument', receiptDocument);
          }

          const response = await BillsService.updateSpecificBill(
            row.original.publicId, 
            formData,
            progressEvent => {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total!);
              setReceiptDocumentProgress(percentCompleted);
            }
          );
          
          if (response.data.result === 'success') {
            // Record transaction if chart of account is selected
            if (selectedAccountId) {
              await recordTransaction(status, amount || row.original.amount);
            }
            
            toast({
              title: `Payment Recorded`,
              description: `Bill #${row.original.billNumber} has been marked as ${status.replace('_', ' ')} and Receipt #${response.data.receiptNumber} has been created.`,
              variant: "default",
            });
            setTimeout(() => {
              window.location.reload();
            }, 1500);
          } else {
            toast({
              title: "Error",
              description: response.data.message,
              variant: "destructive",
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to update bill status",
            variant: "destructive",
          });
        } finally {
          setIsMarking(false);
        }
      };
      
      // Function to record transaction in the accounting system
      const recordTransaction = async (status: string, amount: any) => {
        try {
          // Convert to number and ensure it's a valid value
          const amountValue = typeof amount === 'string' ? parseFloat(amount) : amount;
          
          const transactionData = {
            chartOfAccountId: parseInt(selectedAccountId),
            transactionType: "DEBIT", // Bill payment is a debit to the selected account
            amount: amountValue,
            currency: row.original.currency,
            description: `Payment made for Bill #${row.original.billNumber}`,
            reference: row.original.billNumber,
            referenceId: row.original.id.toString(),
            referenceType: "bill",
            transactionDate: paymentDate,
          };
          
          const response = await TransactionsService.createTransaction(transactionData, selectedBusiness.id);
          
          if (response.data.result === 'success') {
            toast({
              title: "Success",
              description: "The payment has been recorded in the accounting system.",
            });
          } else {
            toast({
              title: "Warning",
              description: "Bill payment recorded but transaction recording failed: " + response.data.message,
              variant: "destructive"
            });
          }
        } catch (error) {
          console.error("Error recording transaction:", error);
          toast({
            title: "Warning",
            description: "Bill payment recorded but transaction recording failed.",
            variant: "destructive"
          });
        }
      };

      const handleReceiptDocumentUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
          setReceiptDocument(file);
        }
      };

      const handleMarkAsPaid = () => {
        if (!paymentMethodId) {
          toast({
            title: "Error",
            description: "Please select a payment method",
            variant: "destructive",
          });
          return;
        }
        updateBillStatus('paid', row.original.amount);
        setModalOpen(false);
      };

      const handleMarkAsPartiallyPaid = () => {
        if (!paymentMethodId) {
          toast({
            title: "Error",
            description: "Please select a payment method",
            variant: "destructive",
          });
          return;
        }
        
        if (!amountPaid || parseFloat(amountPaid) <= 0) {
          toast({
            title: "Error",
            description: "Please enter a valid amount",
            variant: "destructive",
          });
          return;
        }
        
        updateBillStatus('partially_paid', amountPaid);
        setModalOpen(false);
      };

      const isBankTransferPayment: boolean = paymentMethodId === '4';
      const isPaid = row.original.status === 'paid' || row.original.status === 'overpaid';
      // Check if user has permission to edit bills
      const hasEditPermission = canEdit('Bills');
      
      return (
        <>
          <Button 
            onClick={() => setModalOpen(true)} 
            variant="outline"
            disabled={isPaid || isMarking || !hasEditPermission}
            className={`h-9 px-3 flex items-center gap-1.5 ${
              isPaid 
                ? 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400' 
                : 'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100 dark:bg-blue-900/20 dark:border-blue-800 dark:text-blue-400 dark:hover:bg-blue-900/30'
            }`}
          >
            {isMarking ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : isPaid ? (
              <>
                <CheckCircle2 className="w-4 h-4" />
                <span>Paid</span>
              </>
            ) : (
              <>
                <CreditCard className="w-4 h-4" />
                <span>Record Payment</span>
              </>
            )}
          </Button>
          
          <FormModal open={isModalOpen} setOpen={setModalOpen} title="Record Payment">
            <div className="space-y-5 py-2">
              <div>
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-sm font-medium text-slate-700 dark:text-slate-300">Bill Details</h3>
                  <Badge variant="outline" className="text-slate-600 border-slate-300 dark:text-slate-400 dark:border-slate-600">
                    #{row.original.billNumber}
                  </Badge>
                </div>
                <div className="bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Total Amount:</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {row.original.currency} {row.original.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-600 dark:text-slate-400">Due Date:</span>
                    <span className="font-medium text-slate-900 dark:text-slate-100">
                      {new Date(row.original.dueDate).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              
              <Separator className="bg-slate-200 dark:bg-slate-700" />
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Amount Paid</label>
                <Input
                  type="number"
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  placeholder={`${row.original.currency} 0.00`}
                  className="border-slate-300 dark:border-slate-600 focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Leave empty to pay the full amount ({row.original.currency} {row.original.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Payment Method</label>
                {isPaymentMethodsLoaded ? (
                  <select
                    value={paymentMethodId}
                    onChange={(e) => setPaymentMethodId(e.target.value)}
                    className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Payment Method</option>
                    {paymentMethods && paymentMethods.map((method: any) => (
                      <option key={method.id} value={method.id}>{method.name}</option>
                    ))}
                  </select>
                ) : (
                  <div className="flex justify-center items-center py-2 bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-200 dark:border-slate-700">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin text-blue-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Loading payment methods...</span>
                  </div>
                )}
              </div>
              
              {isBankTransferPayment && (
                <div>
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Payment Reference</label>
                  <Input
                    type="text"
                    value={paymentReference}
                    onChange={(e) => setPaymentReference(e.target.value)}
                    placeholder="e.g. Transaction ID or Reference Number"
                    className="border-slate-300 dark:border-slate-600 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              )}
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Payment Date</label>
                <Input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="border-slate-300 dark:border-slate-600 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Custom Receipt Number (Optional)</label>
                <Input
                  type="text"
                  value={customReceiptNumber}
                  onChange={(e) => setCustomReceiptNumber(e.target.value)}
                  placeholder="Leave empty for auto-generated number"
                  className="border-slate-300 dark:border-slate-600 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Chart of Account</label>
                {isChartOfAccountsLoaded ? (
                  <select
                    value={selectedAccountId}
                    onChange={(e) => setSelectedAccountId(e.target.value)}
                    className="w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 py-2 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Account (Optional)</option>
                    {chartOfAccounts.map((account) => (
                      <option key={account.id} value={account.id}>
                        {account.code} - {account.name} ({account.type})
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="flex justify-center items-center py-2 bg-slate-50 dark:bg-slate-800/50 rounded-md border border-slate-200 dark:border-slate-700">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin text-blue-500" />
                    <span className="text-sm text-slate-600 dark:text-slate-400">Loading accounts...</span>
                  </div>
                )}
                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                  Select an account to record this payment as a transaction
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Receipt Document (Optional)</label>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={() => {
                      const input = document.createElement('input');
                      input.type = 'file';
                      input.accept = '.pdf,.png,.jpg,.jpeg';
                      input.onchange = handleReceiptDocumentUpload as any;
                      input.click();
                    }}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {receiptDocument ? receiptDocument.name : 'Upload Receipt Document'}
                  </Button>
                  {receiptDocument && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setReceiptDocument(null)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {receiptDocumentProgress > 0 && receiptDocumentProgress < 100 && (
                  <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${receiptDocumentProgress}%` }}
                    />
                  </div>
                )}
              </div>
              
              <Separator className="bg-slate-200 dark:bg-slate-700" />
              
              <div className="flex justify-end gap-2">
                <Button 
                  onClick={() => setModalOpen(false)} 
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-100 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={handleMarkAsPartiallyPaid} 
                  disabled={!amountPaid || parseFloat(amountPaid) <= 0 || !paymentMethodId}
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                >
                  <CreditCard className="w-4 h-4 mr-1.5" />
                  Record Partial Payment
                </Button>
                <Button 
                  onClick={handleMarkAsPaid} 
                  disabled={!paymentMethodId}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />
                  Pay in Full
                </Button>
              </div>
            </div>
          </FormModal>
        </>
      );
    },
  },
  {
    id: "actions",
    header: "",
    cell: ({ row }) => {
      const { toast } = useToast();
      const [showDeleteDialog, setShowDeleteDialog] = useState(false);
      
      const handleDelete = async () => {
        try {
          const response = await BillsService.deleteBill(row.original.id);
          if (response.data.result === 'success') {
            toast({
              title: "Bill Deleted",
              description: "Bill has been successfully deleted",
              variant: "default",
            });
            setTimeout(() => {
              window.location.reload();
            }, 1000);
          } else {
            toast({
              title: "Error",
              description: response.data.message,
              variant: "destructive",
            });
          }
        } catch (error) {
          toast({
            title: "Error",
            description: "Failed to delete bill",
            variant: "destructive",
          });
        }
      };

      const isDeletableStatus = row.original.status === 'pending' || row.original.status === 'overdue';
      // Check if user has permission to delete bills
      const hasDeletePermission = canDelete('Bills');

      return (
        <div className="flex items-center justify-end gap-2">
          <Button 
            onClick={() => window.location.href = `/bill/${row.original.publicId}`} 
            variant="outline"
            className="h-8 px-3 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-2" />
            View
          </Button>
          
          {isDeletableStatus && hasDeletePermission && (
            <Button 
              onClick={() => setShowDeleteDialog(true)} 
              variant="outline"
              className="h-8 px-3 text-rose-600 dark:text-rose-500 border-rose-200 dark:border-rose-800 hover:bg-rose-50 dark:hover:bg-rose-900/30"
            >
              <Trash className="w-3.5 h-3.5 mr-2" />
              Delete
            </Button>
          )}
          
          <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <AlertDialogContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-700">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-slate-900 dark:text-slate-100">Are you sure?</AlertDialogTitle>
                <AlertDialogDescription className="text-slate-600 dark:text-slate-400">
                  This will permanently delete bill #{row.original.billNumber}. This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="border-slate-200 text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800">
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction 
                  onClick={handleDelete} 
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      );
    }
  }
]
